import React from "react";
import { Card, CardContent, Typography, Box, Chip, Stack } from "@mui/material";
import { Mail, User, ArrowDownLeft, ArrowUpRight, Link } from "lucide-react";
import { ProcessedEmail } from "../types";
import { WorksList } from "./WorksList";
import { EmailAttachments } from "./EmailAttachments";

interface EmailCardProps {
  email: ProcessedEmail;
  emailType?: "incoming" | "outgoing" | "internal" | "all";
  onConfirmMatch: (
    emailId: string,
    workId?: number,
    eventDataId?: number
  ) => void;
}

export const EmailCard: React.FC<EmailCardProps> = ({
  email,
  emailType = "incoming", // Default till inkommande om inte specificerat
  onConfirmMatch,
}) => {
  let directionIcon = null;
  let directionText = "";

  switch (emailType) {
    case "internal":
      directionIcon = (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M8 3H7a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h1" />
          <path d="M16 3h1a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-1" />
          <path d="M12 22v-8" />
          <path d="M12 14 9 11" />
          <path d="m12 14 3-3" />
          <path d="M8 7h8" />
        </svg>
      );
      directionText = "Internt";
      break;
    case "outgoing":
      directionIcon = <ArrowUpRight size={20} />;
      directionText = "Utgående";
      break;
    case "incoming":
    default:
      directionIcon = <ArrowDownLeft size={20} />;
      directionText = "Inkommande";
      break;
  }

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        "&:hover": {
          boxShadow: (theme) => theme.shadows[4],
        },
        overflow: "visible",
        // Add colored border based on direction
        borderLeft: (theme) =>
          `4px solid ${
            emailType === "internal"
              ? theme.palette.secondary.main
              : emailType === "outgoing"
              ? theme.palette.success.main
              : theme.palette.info.main
          }`,
      }}
    >
      {/* Direction indicator */}
      <Box
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          color: (theme) =>
            emailType === "internal"
              ? theme.palette.secondary.main
              : emailType === "outgoing"
              ? theme.palette.success.main
              : theme.palette.info.main,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        {directionIcon}
        <Typography variant="caption" color="inherit">
          {directionText}
        </Typography>
      </Box>

      <CardContent sx={{ flex: 1, pt: 5 }}>
        <Stack spacing={2}>
          {/* Header */}
          <Box>
            <Typography variant="h6" gutterBottom>
              {email.subject || "(Inget ämne)"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(email.receivedDateTime).toLocaleDateString("sv-SE", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </Typography>
          </Box>

          {/* From/To Information */}
          <Stack spacing={1}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Mail size={16} />
              <Typography variant="body2" noWrap>
                {email.from?.emailAddress.name ?? "Ingen avsändare"}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {email.from?.emailAddress.address ?? "Ingen avsändare"}
              </Typography>
            </Box>

            {(email.toRecipients ?? []).map((recipient, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  pl: 3,
                }}
              >
                <User size={16} />
                <Typography variant="body2" noWrap>
                  {recipient.emailAddress.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {recipient.emailAddress.address}
                </Typography>
              </Box>
            ))}
          </Stack>

          {/* Matches */}
          <Box>
            {email.matches.length === 0 ? (
              <Chip
                label="Ingen matchning"
                size="small"
                color="warning"
                sx={{ mt: 1 }}
              />
            ) : (
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {email.matches.map((match, idx) => (
                  <Chip
                    key={idx}
                    icon={<Link size={16} />}
                    label={`${match.customer} ${
                      match.contactPerson ? `(${match.contactPerson})` : ""
                    }`}
                    size="small"
                    color="success"
                  />
                ))}
              </Stack>
            )}
          </Box>

          {/* Attachments */}
          {email.hasAttachments && (
            <Box>
              <EmailAttachments messageId={email.id} />
            </Box>
          )}

          {/* Work Items */}
          {email.matches.map((match, idx) => (
            <Box key={idx}>
              <WorksList
                customerNo={match.customerNo}
                email={email}
                onConfirmMatch={(workId, eventDataId) =>
                  onConfirmMatch(email.id, workId, eventDataId)
                }
              />
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};
